import { Box, Select, Spinner } from '@chakra-ui/react';
import { useEffect } from 'react';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { READ_FAILED_I18N_KEY } from '../../_shared/readState';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useMyOrganizationsReadState } from '../hooks/useGetMyOrganizations';
import { useSelectedOrganization } from '../hooks/useSelectedOrganization';

export default function OrganizationPicker() {
    // This picker is the root of every org-scoped read in the dashboard: 62
    // hooks are gated `enabled: !!organizationId`, and the only thing that ever
    // sets that id is the auto-select below or a choice made in this Select. It
    // took no error flag until run 125, so a refused `my-organizations` painted
    // as a chooser with no options — an operator with organizations was told,
    // in the navbar of every page, that they had none, and every page behind it
    // then rendered its own "select an organization first" prompt about a read
    // that had failed. Nothing anywhere said so.
    //
    // The classification lives in the hook (`useMyOrganizationsReadState`), not
    // here. Run 125 derived it at this call site as
    // `classifyRead(isError, isInitialLoading || isPlaceholderData, data)`,
    // because this hook's `placeholderData: []` makes both of the usual loading
    // flags false while the real list is on the wire; run 126 needed the same
    // term on `OrganizationPage` and moved it up rather than write it twice.
    // D59 — a second copy of a shared decision is a fork, and this one would
    // fail silently, since a caller reaching for the obvious `isInitialLoading`
    // is wrong only mid-flight. The hook's docblock carries the measurement.
    const { state: readState, organizations } = useMyOrganizationsReadState();
    const { setSelectedLocation } = useSelectedLocation();
    const { setSelectedOrganization, selectedOrganization: selectedOrg } = useSelectedOrganization();

    useEffect(() => {
        // Only auto-select a default when nothing is selected yet. Without the
        // guard, every background refetch of the organizations list hands back a
        // fresh array reference and snaps the picker back to organizations[0] —
        // overriding an admin's explicit choice mid-session.
        if (!selectedOrg && organizations && organizations.length > 0) {
            setSelectedOrganization(organizations[0]);
        }
    }, [organizations, selectedOrg]);

    if (readState === 'loading') {
        return <Spinner />;
    }

    if (readState !== 'answered') {
        // `unreadable` — and `never-issued`, which this call site cannot reach
        // (the hook's `enabled` defaults to true and nothing here passes
        // false). Both render the same notice deliberately: the alternative is
        // the empty Select below, and an empty chooser is a definitive claim
        // about which organizations exist. A refused read and an unasked one
        // are entitled to make no claim at all (D57).
        //
        // `classifyRead`'s `unreadable` requires `!data`, so a failed
        // BACKGROUND refetch keeps a good list on screen rather than replacing
        // the navbar of every page with an error (R96-3).
        return <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />;
    }

    return (
        <Box>
            <Select
                borderRadius={50}
                placeholder={translate('organization.select_organization')}
                value={selectedOrg?.id}
                onChange={(e) => {
                    setSelectedOrganization(organizations?.find(o => o.id === e.target.value))
                    setSelectedLocation(undefined);
                }}
            >
                {organizations?.map((org) => (
                    <option key={org.id} value={org.id}>
                        {org.name}
                    </option>
                ))}
            </Select>
        </Box>
    );
}
